import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Chip,
    Autocomplete,
    TextField,
    Avatar,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

const category_images = {
    "Appetizers & Sides": "f4d162babed3adfbc07921679d84d368.png",
    "Burgers": "7990f92cc9e91087bfa36af0fb64d05a.png",
    "Sandwiches & Wraps": "c8c405f19b567b18d6a53e646529ec05.png",
    "Pizzas": "26202dabbbc256097dd558e61f5163c5.png",
    "Pastas": "f7d284c34b0fa07b791ca3f2e6a9dbcf.png",
    "Salads": "69c7ea267011b736da7e600ae0205cf1.png",
    "Soups & Stews": "fa78151e8761a93e868f72fc770a093b.png",
    "Seafood": "d9b38b5bed15bc6eea6935e32c22da58.png",
    "Chicken Dishes": "45e8036e6494825439c7c785ef41e1dd.png",
    "Beef Dishes": "8d5ed041b019e011bd10f5aff7b2fc56.png",
    "Pork Dishes": "a27599bc1b82ac86a891bb05c8da5c19.png",
    "Vegetarian & Vegan": "2da59f1cc661a341d6add1c526755075.png",
    "Vegan": "71a731effc43ac8bab7455c5e4ce9cc5.png",
    "Breakfast & Brunch": "71881a4d1971eb4ba6986261a49e0173.png",
    "Egg Dishes": "1f91f534f2d181089e07b7cb93c0bdda.png",
    "Rice Bowls": "cbb26e76391afbcd71a9cbccc9720be8.png",
    "Noodles": "785b95b3572eb0ac40598fbc7a8e12e4.png",
    "Tacos & Burritos": "6b7ccfc6e4ab64f5412bc8ff5057994a.png",
    "Quesadillas": "029c93b90d51ab369a7d29aed9f2b193.png",
    "Nachos": "9b173701e7f489098d6622b7cb43194a.png",
    "BBQ & Grilled Meats": "d767d393cdeb3f90ce42cd99c2f61760.png",
    "Fried Foods": "b8a8b30849a94c4036dd5ab1d4d6c316.png",
    "Bakery & Pastries": "b8964c91ab2804954aa4b3ca97c85c09.png",
    "Desserts": "66ea83dd5375f75118335d2b82e2fb29.png",
    "Ice Cream & Frozen Treats": "a5ca25b5d1babf39c7a25a2ef7d97834.png",
    "Combo Food": "4d52446038e423b5bee64af2ad8bd6ef.png",
    "Sauces": "815bbeaca8e0456f0cf50cd003507e53.png",
    "Beverages - Hot": "640819e4e75a4ef82ef6871e9e52f9fa.png",
    "Beverages - Cold": "0ec36c9f4edc68cdac549a8ff64ba480.png",
    "Smoothies & Shakes": "65c5b81ea265af25b080e93199bfa6c5.png",
    "Alcoholic Drinks": "f50daf8c97a0d6f751e83b6dbb2489de.png",
    "Kids Menu": "3b20b6ff86e1439f1e423ce40c811751.png",
    "Combo Meals": "d05e8c3ed994e77e677c276a419c1fb1.png",
    "Specialty Items": "bea40633d81970b07ad714e8d3f53ccf.png"
};

export const ExploreProductsPopup = ({ open, onClose, boundingbox }) => {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [expandedProducts, setExpandedProducts] = useState({});
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [allCategories, setAllCategories] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'accordion'

    useEffect(() => {
        if (open && boundingbox) {
            fetchProducts();
        }
    }, [open]);

    const fetchProducts = async () => {
        if (!boundingbox || !Array.isArray(boundingbox) || boundingbox.length === 0) {
            console.error('No valid bounds provided');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("auth-token");
            const response = await axios.post('/gettheurl/product-map-search', {
                bounds: boundingbox,
                limit: 1000
            }, {
                headers: { "auth-token": token }
            });
            
            if (response.data.success && response.data.data && response.data.data.length > 0) {
                setProducts(response.data.data);
                
                const categories = [...new Set(response.data.data.map(product => 
                    product.category || product.clean_category
                ).filter(Boolean))];
                
                setAllCategories(categories);
                setSelectedCategories(categories);
                setFilteredProducts(response.data.data);
            } else {
                setProducts([]);
                setAllCategories([]);
                setSelectedCategories([]);
                setFilteredProducts([]);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]);
            setAllCategories([]);
            setSelectedCategories([]);
            setFilteredProducts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        applyFilters();
    }, [products, selectedCategories, searchTerm]);

    const applyFilters = () => {
        let filtered = products.filter(product => {
            const productCategory = product.category || product.clean_category;
            const matchesCategory = selectedCategories.includes(productCategory);
            const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                product.brand?.toLowerCase().includes(searchTerm.toLowerCase());
            
            return matchesCategory && matchesSearch;
        });

        setFilteredProducts(filtered);
    };

    const formatPrice = (priceData) => {
        if (!priceData) return 'Price not available';
        
        if (typeof priceData === 'object') {
            if (priceData.min !== undefined && priceData.max !== undefined) {
                return `$${priceData.min.toFixed(2)} - $${priceData.max.toFixed(2)}`;
            } else if (priceData.price !== undefined) {
                return `$${priceData.price.toFixed(2)}`;
            }
        }
        
        return typeof priceData === 'number' ? `$${priceData.toFixed(2)}` : priceData;
    };

    const handleProductToggle = (productIndex) => {
        setExpandedProducts(prev => ({
            ...prev,
            [productIndex]: !prev[productIndex]
        }));
    };

    const getBrandImage = (product) => {
        // If product has an Image field, use it
        if (product.Image && product.Image.trim()) {
            return product.Image;
        }
        // Otherwise, try to get brand image from brand name
        if (product.brand) {
            return `/brand_images/${product.brand.toLowerCase().replace(/\s+/g, '_')}.png`;
        }
        // Return null if no image available
        return null;
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedProducts = filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{ '& .MuiDialog-paper': { minHeight: '80vh', maxHeight: '90vh' } }}
        >
            <DialogTitle sx={{ pb: 2, pt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                    Explore Products
                </Typography>
                <IconButton onClick={onClose} sx={{ ml: 1 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
                {/* Search and Filter Controls */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, mt: 1 }}>
                    <TextField
                        label="Search Products"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                        }}
                        sx={{ flex: 1, minWidth: 250 }}
                    />
                    
                    <Autocomplete
                        multiple
                        options={allCategories}
                        value={selectedCategories}
                        onChange={(event, newValue) => setSelectedCategories(newValue)}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip 
                                    key={index}
                                    variant="outlined" 
                                    label={option} 
                                    {...getTagProps({ index })} 
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter Categories"
                                placeholder="Select categories"
                            />
                        )}
                        sx={{ minWidth: 300, flex: 1 }}
                    />
                </Box>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <Typography>Loading products...</Typography>
                    </Box>
                )}

                {/* Products Table */}
                {!loading && (
                    <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                        {filteredProducts.length === 0 ? (
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                <Typography color="text.secondary">
                                    {searchTerm || selectedCategories.length < allCategories.length 
                                        ? 'No products match your filters' 
                                        : 'No products available'}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Variations</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedProducts.map((product, index) => (
                                                <TableRow 
                                                    key={index} 
                                                    sx={{ 
                                                        '&:hover': { backgroundColor: '#f9f9f9' },
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => handleProductToggle(page * rowsPerPage + index)}
                                                >
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar
                                                                src={getBrandImage(product)}
                                                                sx={{ width: 32, height: 32 }}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            >
                                                                {product.brand?.[0]?.toUpperCase() || 'B'}
                                                            </Avatar>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {product.brand || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {product.name || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={product.category || product.clean_category || 'N/A'} 
                                                            size="small" 
                                                            color="primary" 
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                                            {formatPrice(product.clean_price)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                                                            {product.address || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {product.variations && Object.keys(product.variations).length > 0 
                                                                ? `${Object.keys(product.variations).length} variations`
                                                                : 'No variations'
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Pagination */}
                                <TablePagination
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    component="div"
                                    count={filteredProducts.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />

                                {/* Expanded Product Details */}
                                {Object.keys(expandedProducts).map(productIndex => {
                                    if (!expandedProducts[productIndex]) return null;
                                    
                                    const product = filteredProducts[productIndex];
                                    if (!product || !product.variations || Object.keys(product.variations).length === 0) return null;
                                    
                                    return (
                                        <Box key={productIndex} sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                            <Typography variant="h6" gutterBottom>
                                                {product.name} - Variations
                                            </Typography>
                                            
                                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Variation</TableCell>
                                                            <TableCell align="right">Price</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {Object.entries(product.variations).map(([variationName, price], idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{variationName}</TableCell>
                                                                <TableCell align="right">
                                                                    {price ? formatPrice(price) : 'N/A'}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    );
                                })}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>
            
        </Dialog>
    );
};